import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogTitle,
    Divider,
    IconButton,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import CustomSnackbar from "../Common/CustomSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDeviceCreate } from "./hooks/useDeviceCreate";
import { useSnackbar } from "../Common/hooks/useSnackbar";
import { deviceIcons, getDeviceIcon } from "../../config/deviceIcons";

type Prop = {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
};

const DeviceCreate: React.FC<Prop> = ({ open, onClose, onSuccess }) => {
    const { showSuccess, showError, snackbarProps } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const {
        deviceName, setDeviceName,
        serialNumber, setSerialNumber,
        description, setDescription,
        parserConfigs, selectedParserConfig, setSelectedParserConfig,
        deviceTypes, selectedDeviceType, setSelectedDeviceType,
        deviceGroups, selectedDeviceGroup, setSelectedDeviceGroup,
        iconId, setIconId,
        errors, clearError,
        handleCreate, resetForm,
    } = useDeviceCreate(open);

    const handleSubmit = async () => {
        try {
            const successMessage = await handleCreate();
            showSuccess(successMessage);
            resetForm();
            onClose();
            onSuccess && onSuccess();
        } catch (error) {
            if ((error as Error).message) showError(error as Error);
        }
    };

    const SelectedIcon = getDeviceIcon(iconId);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography fontWeight={600} fontSize={18}>Cihaz Ekle</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <TextField
                            size="small"
                            label="Cihaz Adı *"
                            value={deviceName}
                            onChange={(e) => { setDeviceName(e.target.value); clearError("deviceName"); }}
                            error={!!errors.deviceName}
                            helperText={errors.deviceName}
                            sx={{ flex: 1 }}
                        />
                        <Box
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                border: Boolean(anchorEl) ? "1px solid var(--color-primary)" : "1px solid #e0e0e0",
                                borderRadius: "8px",
                                px: 1,
                                flexShrink: 0,
                                minWidth: 72,
                                height: 40,
                                boxSizing: "border-box",
                                "&:hover": { borderColor: "var(--color-primary)" },
                            }}
                        >
                            <SelectedIcon sx={{ fontSize: 22, color: "var(--color-primary)" }} />
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: 18,
                                    color: "text.secondary",
                                    transform: Boolean(anchorEl) ? "rotate(180deg)" : "none",
                                    transition: "transform 0.2s",
                                }}
                            />
                        </Box>
                    </Box>

                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        slotProps={{ paper: { sx: { p: 1.5, borderRadius: 2 } } }}
                    >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 240 }}>
                            {deviceIcons.map((cfg) => {
                                const IconCmp = cfg.icon;
                                const selected = iconId === cfg.id;
                                return (
                                    <Tooltip key={cfg.id} title={cfg.label}>
                                        <Box
                                            onClick={() => { setIconId(cfg.id); setAnchorEl(null); }}
                                            sx={{
                                                width: 40, height: 40,
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                cursor: "pointer", borderRadius: "8px",
                                                border: selected ? "2px solid var(--color-primary)" : "2px solid transparent",
                                                bgcolor: selected ? "var(--color-primary-alpha-07)" : "transparent",
                                                "&:hover": { bgcolor: "var(--color-primary-alpha-07)" },
                                                transition: "all 0.15s",
                                            }}
                                        >
                                            <IconCmp sx={{ fontSize: 22, color: "var(--color-primary)" }} />
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Popover>

                    <TextField
                        fullWidth size="small"
                        label="Seri No *"
                        value={serialNumber}
                        onChange={(e) => { setSerialNumber(e.target.value); clearError("serialNumber"); }}
                        error={!!errors.serialNumber}
                        helperText={errors.serialNumber}
                    />
                    <Autocomplete
                        size="small"
                        options={deviceTypes}
                        value={selectedDeviceType}
                        getOptionLabel={(option) => option.name}
                        onChange={(_, newValue) => { setSelectedDeviceType(newValue); clearError("selectedDeviceType"); }}
                        renderInput={(params) => (
                            <TextField {...params} label="Cihaz Tipi *" placeholder="Cihaz tipi seçiniz"
                                error={!!errors.selectedDeviceType} helperText={errors.selectedDeviceType} />
                        )}
                    />
                    <Autocomplete
                        size="small"
                        options={parserConfigs}
                        value={selectedParserConfig}
                        getOptionLabel={(option) => option.name}
                        onChange={(_, newValue) => { setSelectedParserConfig(newValue); clearError("selectedParserConfig"); }}
                        renderInput={(params) => (
                            <TextField {...params} label="Parser *" placeholder="Parser seçiniz"
                                error={!!errors.selectedParserConfig} helperText={errors.selectedParserConfig} />
                        )}
                    />
                    <Autocomplete
                        size="small"
                        options={deviceGroups}
                        value={selectedDeviceGroup}
                        getOptionLabel={(option) => option.name}
                        onChange={(_, newValue) => setSelectedDeviceGroup(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Cihaz Grubu" placeholder="Grup seçiniz (opsiyonel)" />
                        )}
                    />
                    <TextField
                        fullWidth size="small"
                        label="Açıklama"
                        value={description}
                        slotProps={{ htmlInput: { maxLength: 150 } }}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
                        <Button variant="outlined" onClick={onClose}
                            sx={{ textTransform: "none", borderRadius: "6px", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}>
                            İptal
                        </Button>
                        <Button variant="contained" onClick={handleSubmit}
                            sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
                            Kaydet
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <CustomSnackbar {...snackbarProps} />
        </>
    );
};

export default DeviceCreate;
